import { generateDict } from '../helpers/strings';
export class TrackingHandler {
    static instance;
    providers = [];
    listeners = [];
    consent = {
        essential: false,
        functional: false,
        marketing: false,
    };
    viewTimestamp = new Date().getTime();
    initializers = [];
    experimentVariations = {};
    msBeforeFirstScrollEvent = 1000;
    msToScrollEnd = 500;
    minimumScrollDistance = 10;
    maxNumScrollEvents = 20;
    scrollBreakpointsPercent = [1, 10, 25, 50, 75, 100];
    reportedBreakpoint = 0;
    consentGrantedResolvers = {
        essential: [],
        functional: [],
        marketing: [],
    };
    constructor() {
        this.trackScrollEvents();
    }
    static getInstance() {
        if (!TrackingHandler.instance) {
            TrackingHandler.instance = new TrackingHandler();
        }
        return TrackingHandler.instance;
    }
    trackScrollEvents() {
        let timeout = null;
        let scrollStartTime = null;
        let scrollPosition = 0;
        let numScrollEvents = 0;
        const listener = () => {
            if (!scrollStartTime) {
                scrollStartTime = new Date().getTime();
            }
            if (timeout) {
                window.clearTimeout(timeout);
            }
            timeout = window.setTimeout(handleScrollUpdate, this.msToScrollEnd);
        };
        const getScrollPercentage = (scrollPosition) => {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            return Math.ceil((scrollPosition / maxScroll) * 100);
        };
        const handleScrollPercentage = () => {
            const currentScrollPercent = getScrollPercentage(window.scrollY);
            let highestReportableBreakpoint = 0;
            for (const breakpoint of this.scrollBreakpointsPercent) {
                if (breakpoint > currentScrollPercent) {
                    break;
                }
                highestReportableBreakpoint = breakpoint;
            }
            if (highestReportableBreakpoint <= this.reportedBreakpoint) {
                return;
            }
            this.reportedBreakpoint = highestReportableBreakpoint;
            this.scrollPercentage(highestReportableBreakpoint);
        };
        const handleScrollUpdate = () => {
            handleScrollPercentage();
            const scrollChange = window.scrollY - scrollPosition;
            const scrollDistance = Math.abs(scrollChange);
            if (scrollDistance < this.minimumScrollDistance) {
                scrollStartTime = new Date().getTime();
                return;
            }
            const scrollDuration = scrollStartTime ? new Date().getTime() - scrollStartTime : 0;
            numScrollEvents++;
            this.scrollDistance(scrollPosition, getScrollPercentage(scrollPosition), window.scrollY, getScrollPercentage(window.scrollY), scrollDistance, Math.abs(getScrollPercentage(scrollDistance)), scrollChange > 0 ? 'down' : 'up', scrollDuration, numScrollEvents);
            if (numScrollEvents >= this.maxNumScrollEvents) {
                document.removeEventListener('scroll', listener);
            }
            scrollPosition = window.scrollY;
            scrollStartTime = null;
        };
        window.setTimeout(() => {
            scrollPosition = window.scrollY;
            document.addEventListener('scroll', listener);
        }, this.msBeforeFirstScrollEvent);
    }
    get visitId() {
        for (const provider of this.providers) {
            const providerValue = provider.visitId;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get visitTimestamp() {
        for (const provider of this.providers) {
            const providerValue = provider.visitTimestamp;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get visitorId() {
        for (const provider of this.providers) {
            const providerValue = provider.visitorId;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get visitorTimestamp() {
        for (const provider of this.providers) {
            const providerValue = provider.visitorTimestamp;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get viewId() {
        for (const provider of this.providers) {
            const providerValue = provider.viewId;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get source() {
        for (const provider of this.providers) {
            const providerValue = provider.source;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get medium() {
        for (const provider of this.providers) {
            const providerValue = provider.medium;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get campaign() {
        for (const provider of this.providers) {
            const providerValue = provider.campaign;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get pageProduct() {
        for (const provider of this.providers) {
            const providerValue = provider.pageProduct;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get pageLocation() {
        for (const provider of this.providers) {
            const providerValue = provider.pageLocation;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get conversionPixels() {
        for (const provider of this.providers) {
            const providerValue = provider.conversionPixels;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get fingerprint() {
        for (const provider of this.providers) {
            const providerValue = provider.fingerprint;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get currentUrl() {
        for (const provider of this.providers) {
            const providerValue = provider.currentUrl;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    get originDetails() {
        let originDetailsMerged = {};
        for (const provider of this.providers) {
            const providerValue = provider.originDetails;
            if (providerValue === null) {
                continue;
            }
            originDetailsMerged = { ...providerValue, ...originDetailsMerged };
        }
        if (Object.entries(this.experimentVariations).length > 0) {
            originDetailsMerged.experimentVariations = this.experimentVariations;
        }
        return originDetailsMerged;
    }
    get isCustomer() {
        for (const provider of this.providers) {
            const providerValue = provider.isCustomer;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return false;
    }
    get referralCode() {
        for (const provider of this.providers) {
            const providerValue = provider.referralCode;
            if (providerValue !== null) {
                return providerValue;
            }
        }
        return null;
    }
    chatEnd() {
        for (const listener of this.listeners) {
            listener.chatEnd();
        }
    }
    chatStart() {
        for (const listener of this.listeners) {
            listener.chatStart();
        }
    }
    formNavigate(formName, formStep, navigationAction, data) {
        for (const listener of this.listeners) {
            listener.formNavigate(formName, formStep, navigationAction, data);
        }
    }
    formStart(formName, inputName) {
        for (const listener of this.listeners) {
            listener.formStart(formName, inputName);
        }
    }
    formSubmit(formName, data) {
        for (const listener of this.listeners) {
            listener.formSubmit(formName, data);
        }
    }
    pageReady() {
        for (const listener of this.listeners) {
            listener.pageReady();
        }
    }
    scrollDistance(from, fromPercent, to, toPercent, distance, distancePercent, direction, duration, numEvent) {
        for (const listener of this.listeners) {
            listener.scrollDistance(from, fromPercent, to, toPercent, distance, distancePercent, direction, duration, numEvent);
        }
    }
    scrollPercentage(percent) {
        for (const listener of this.listeners) {
            listener.scrollPercentage(percent);
        }
    }
    get essentialConsent() {
        return this.consent.essential;
    }
    get functionalConsent() {
        return this.consent.functional;
    }
    get marketingConsent() {
        return this.consent.marketing;
    }
    get initialized() {
        return Promise.all(this.initializers);
    }
    setConsent(essential, functional, marketing) {
        this.consent = { essential, functional, marketing };
        for (const listener of this.listeners) {
            listener.setConsent(essential, functional, marketing);
        }
        for (const category of ['essential', 'functional', 'marketing']) {
            if (!this.consent[category]) {
                continue;
            }
            for (const resolver of this.consentGrantedResolvers[category]) {
                resolver();
            }
        }
    }
    setCustomer() {
        for (const listener of this.listeners) {
            listener.setCustomer();
        }
    }
    async setExperimentVariation(experiment, variation) {
        await this.initialized;
        for (const listener of this.listeners) {
            listener.setExperimentVariation(experiment, variation);
        }
        this.experimentVariations[experiment] = variation;
    }
    async getExperimentVariation(experiment, variations, experimentId = 1) {
        await this.initialized;
        if (!this.fingerprint) {
            return variations[0];
        }
        const sliceLength = 2;
        if (experimentId > this.fingerprint.length / sliceLength) {
            console.error('Error: Only ' +
                this.fingerprint.length / sliceLength +
                ' different experiments are supported at the same time at the moment');
            return 'error';
        }
        const sliceStart = (experimentId - 1) * sliceLength;
        const segment = this.fingerprint.slice(sliceStart, sliceStart + sliceLength);
        const dictionary = generateDict(48, 57).concat(generateDict(97, 102));
        const variationNumber = parseInt(segment, 16);
        const variationIndex = Math.floor((variationNumber / Math.pow(dictionary.length, sliceLength)) * variations.length);
        const variation = variations[variationIndex];
        if (!variation) {
            console.error('Could not determine experiment variation');
            return '';
        }
        this.setExperimentVariation(experiment, variation);
        return variation;
    }
    getAllTrackingData() {
        return {
            visitId: this.visitId,
            visitorId: this.visitorId,
            fingerprint: this.fingerprint,
            viewId: this.viewId,
            source: this.source,
            medium: this.medium,
            campaign: this.campaign,
            pageProduct: this.pageProduct,
            pageLocation: this.pageLocation,
            currentUrl: this.currentUrl,
            originDetails: this.originDetails,
            isCustomer: this.isCustomer,
            conversionPixels: this.conversionPixels,
            referralCode: this.referralCode,
        };
    }
    getSessionTrackingData() {
        return {
            visitId: this.visitId,
            source: this.source,
            medium: this.medium,
            campaign: this.campaign,
            pageProduct: this.pageProduct,
            pageLocation: this.pageLocation,
            currentUrl: this.currentUrl,
            originDetails: this.originDetails,
            conversionPixels: this.conversionPixels,
            referralCode: this.referralCode,
        };
    }
    getVisitorTrackingData() {
        return {
            fingerprint: this.fingerprint,
            visitorId: this.visitorId,
            isCustomer: this.isCustomer,
        };
    }
    getViewTrackingData() {
        return {
            viewId: this.viewId,
        };
    }
    consentGranted(category) {
        if (this.consent[category]) {
            return new Promise((resolve) => resolve());
        }
        return new Promise((resolve) => {
            this.consentGrantedResolvers[category].push(resolve);
        });
    }
}
